import { X, Crown, Phone, Mail, Sparkles, Check } from 'lucide-react';
import { Button } from './ui/button';

interface ContactUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  errorMessage?: string;
  remainingViews?: number;
}

export const ContactUpgradeModal = ({ 
  isOpen, 
  onClose, 
  onUpgrade,
  errorMessage,
  remainingViews = 0
}: ContactUpgradeModalProps) => {
  if (!isOpen) return null;

  // Determine the type of error
  const isOutOfViews = errorMessage?.toLowerCase().includes('views') || 
                       errorMessage?.toLowerCase().includes('contact') && remainingViews === 0;
  const isNoPlan = errorMessage?.toLowerCase().includes('subscription') || 
                   errorMessage?.toLowerCase().includes('plan') ||
                   errorMessage?.toLowerCase().includes('active plan');

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleUpgradeClick = () => {
    onClose();
    onUpgrade();
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-in fade-in duration-200"
        onClick={handleBackdropClick}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden pointer-events-auto animate-in zoom-in-95 duration-300">
          {/* Header with gradient */}
          <div className="relative bg-gradient-to-br from-rose-500 via-pink-500 to-purple-600 p-8 text-white overflow-hidden">
            {/* Decorative circles */}
            <div className="absolute top-0 right-0 size-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 size-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors z-10"
            >
              <X className="size-6" />
            </button>
            
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
                  <Crown className="size-8 fill-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Unlock Contact Details</h2>
                  <p className="text-white/90 text-sm">Get access to phone and email</p>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Message */}
            <div className="text-center">
              {isOutOfViews ? (
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-2 rounded-full text-sm font-medium">
                    <Sparkles className="size-4" />
                    No Contact Views Remaining
                  </div>
                  <p className="text-gray-600 text-sm mt-3">
                    You've used all your contact views. Upgrade your plan to view more contact details and connect with potential matches!
                  </p>
                </div>
              ) : isNoPlan ? (
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2 bg-rose-50 text-rose-700 px-4 py-2 rounded-full text-sm font-medium">
                    <Crown className="size-4" />
                    Premium Feature
                  </div>
                  <p className="text-gray-600 text-sm mt-3">
                    Viewing contact details is a premium feature. Subscribe to a plan to unlock contact information and start meaningful conversations!
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2 bg-purple-50 text-purple-700 px-4 py-2 rounded-full text-sm font-medium">
                    <Sparkles className="size-4" />
                    Upgrade Required
                  </div>
                  <p className="text-gray-600 text-sm mt-3">
                    {errorMessage || 'Upgrade your plan to view contact details and connect with your matches!'}
                  </p>
                </div>
              )}
            </div>

            {/* Benefits */}
            <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-xl p-5 space-y-3">
              <p className="font-semibold text-gray-900 text-sm">With a Premium Plan, you get:</p>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 bg-rose-500 rounded-full p-0.5">
                    <Check className="size-3 text-white" strokeWidth={3} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-700"><span className="font-medium">Unlimited Contact Views</span></p>
                    <p className="text-xs text-gray-500">Access phone numbers and emails instantly</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 bg-rose-500 rounded-full p-0.5">
                    <Check className="size-3 text-white" strokeWidth={3} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-700"><span className="font-medium">Priority Matching</span></p>
                    <p className="text-xs text-gray-500">Your profile gets shown first</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 bg-rose-500 rounded-full p-0.5">
                    <Check className="size-3 text-white" strokeWidth={3} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-700"><span className="font-medium">Send More Interests</span></p>
                    <p className="text-xs text-gray-500">Connect with unlimited profiles</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 bg-rose-500 rounded-full p-0.5">
                    <Check className="size-3 text-white" strokeWidth={3} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-700"><span className="font-medium">Advanced Search Filters</span></p>
                    <p className="text-xs text-gray-500">Find your perfect match faster</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={onClose}
                variant="outline"
                className="flex-1 border-gray-300 hover:bg-gray-50"
              >
                Maybe Later
              </Button>
              <Button
                onClick={handleUpgradeClick}
                className="flex-1 bg-gradient-to-r from-rose-500 via-pink-500 to-purple-600 hover:from-rose-600 hover:via-pink-600 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all"
              >
                <Crown className="size-4 mr-2" />
                Upgrade Now
              </Button>
            </div>

            {/* Footer note */}
            <p className="text-center text-xs text-gray-500">
              Choose from flexible plans starting at ₹999/month
            </p>
          </div>
        </div>
      </div>
    </>
  );
};
